import React from "react";
import LegalLayout, { Section } from "../Legal/LegalLayout";
import LEGAL from "../Legal/legalIdentity";

/**
 * Política de Privacidad — página pública (/privacy).
 * URL usada como "Política de Privacidad" y "Eliminación de datos" en la
 * revisión de la app de Meta (ancla #eliminacion-de-datos).
 */
const PrivacyPolicy = () => {
  return (
    <LegalLayout
      title="Política de Privacidad"
      intro={`Esta Política de Privacidad describe cómo ${LEGAL.brand}, operado por ${LEGAL.operator} (${LEGAL.tradeName}), RUC ${LEGAL.ruc}, recopila, utiliza, almacena y protege la información personal en el marco de la Ley N.° 29733 (Ley de Protección de Datos Personales del Perú) y su reglamento, así como de las Condiciones de la Plataforma de Meta cuando corresponda.`}
    >
      <Section id="responsable" title="1. Responsable del tratamiento">
        <p>
          El responsable del tratamiento de los datos personales es{" "}
          <strong>{LEGAL.operator}</strong> ({LEGAL.tradeName}), RUC {LEGAL.ruc}, con
          domicilio en {LEGAL.address}. Para cualquier consulta relativa a privacidad
          puede escribir a <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>.
        </p>
      </Section>

      <Section id="alcance" title="2. Alcance">
        <p>
          Esta política aplica al servicio {LEGAL.brand} (la «Plataforma»), un sistema de
          atención y mensajería omnicanal que permite a las empresas usuarias gestionar
          conversaciones de WhatsApp, Facebook Messenger, Instagram y otros canales que
          conecten. Aplica a los titulares de una cuenta («Usuarios») y a las personas
          cuyos datos se procesan a través de dichos canales.
        </p>
      </Section>

      <Section id="datos" title="3. Información que recopilamos">
        <p>Recopilamos las siguientes categorías de información:</p>
        <ul>
          <li>
            <strong>Datos de registro y cuenta:</strong> nombre de la empresa, nombre del
            titular, correo electrónico, número de teléfono/WhatsApp y contraseña (almacenada
            de forma cifrada).
          </li>
          <li>
            <strong>Datos de los canales de mensajería que el Usuario conecta:</strong>
            identificadores de cuenta y de página, nombres de perfil, fotos de perfil,
            contenido de los mensajes, archivos multimedia y metadatos (fecha, estado de
            entrega, etc.) provenientes de WhatsApp, Facebook Messenger, Instagram u otras
            integraciones que el Usuario habilite.
          </li>
          <li>
            <strong>Datos de la Plataforma de Meta:</strong> cuando el Usuario conecta cuentas
            de Meta (Facebook/Instagram/WhatsApp Business), recibimos datos a través de las APIs
            de Meta necesarios para prestar el servicio (ver sección 5).
          </li>
          <li>
            <strong>Datos técnicos y de uso:</strong> dirección IP, identificadores de sesión,
            registros de actividad (logs) y cookies estrictamente necesarias para autenticación
            y funcionamiento.
          </li>
        </ul>
      </Section>

      <Section id="finalidades" title="4. Finalidades del tratamiento">
        <p>Utilizamos la información para:</p>
        <ul>
          <li>Crear y administrar la cuenta del Usuario y autenticar el acceso.</li>
          <li>
            Recibir, mostrar, enviar y organizar mensajes y tickets de atención entre el
            Usuario y sus contactos a través de los canales conectados.
          </li>
          <li>
            Habilitar funciones del producto (asignación a agentes, respuestas rápidas,
            automatizaciones, campañas y asistentes de IA) configuradas por el Usuario.
          </li>
          <li>Prestar soporte técnico, garantizar la seguridad y prevenir el fraude o el abuso.</li>
          <li>Cumplir obligaciones legales y las políticas de las plataformas integradas.</li>
        </ul>
      </Section>

      <Section id="meta" title="5. Datos de la Plataforma de Meta">
        <p>
          Cuando el Usuario conecta cuentas de Meta, accedemos únicamente a los datos
          necesarios para operar la mensajería (por ejemplo, mensajes entrantes/salientes,
          identificadores y perfil público básico del remitente). Respecto de esos datos:
        </p>
        <ul>
          <li>Los usamos <strong>solo</strong> para prestar y mejorar la funcionalidad de atención que el Usuario solicita.</li>
          <li><strong>No vendemos</strong> datos de la Plataforma de Meta ni los cedemos con fines publicitarios de terceros.</li>
          <li>Cumplimos las Condiciones de la Plataforma de Meta y las políticas de WhatsApp Business, Messenger e Instagram.</li>
          <li>Conservamos esos datos solo el tiempo necesario para la finalidad indicada y los eliminamos según la sección 9.</li>
        </ul>
      </Section>

      <Section id="base-legal" title="6. Base legal y consentimiento">
        <p>
          El tratamiento se sustenta en el consentimiento del titular, en la ejecución del
          contrato de servicio con el Usuario y en el cumplimiento de obligaciones legales.
          El Usuario declara contar con la autorización necesaria de sus propios contactos
          para procesar sus datos a través de la Plataforma.
        </p>
      </Section>

      <Section id="terceros" title="7. Con quién compartimos la información">
        <p>
          No vendemos información personal. Podemos compartirla con proveedores que nos
          ayudan a prestar el servicio, bajo obligaciones de confidencialidad y solo en lo
          necesario:
        </p>
        <ul>
          <li><strong>Proveedor de infraestructura/servidores</strong> donde se aloja la Plataforma.</li>
          <li><strong>Meta Platforms, Inc.</strong> para el envío y recepción de mensajes por sus APIs.</li>
          <li><strong>Proveedores de modelos de IA</strong> (por ejemplo, para funciones de asistente), cuando el Usuario habilita dichas funciones.</li>
          <li>Autoridades competentes cuando exista una obligación legal.</li>
        </ul>
      </Section>

      <Section id="conservacion" title="8. Conservación de datos">
        <p>
          Conservamos los datos mientras la cuenta esté activa y durante el plazo necesario
          para cumplir las finalidades descritas o las obligaciones legales aplicables. Al
          cerrarse la cuenta, los datos se eliminan o anonimizan dentro de un plazo razonable,
          salvo obligación legal de conservación.
        </p>
      </Section>

      <Section id="eliminacion-de-datos" title="9. Eliminación de datos">
        <p>
          El Usuario puede solicitar la eliminación de sus datos y de los datos procesados a
          través de sus canales conectados escribiendo a{" "}
          <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a> con el asunto «Eliminación de
          datos» e indicando la cuenta o el identificador correspondiente. Atenderemos la
          solicitud y eliminaremos la información asociada en un plazo razonable, salvo que
          exista una obligación legal de conservarla. También puede desconectar en cualquier
          momento una cuenta de Meta desde el panel de la Plataforma o desde la configuración
          de su cuenta en Facebook/Instagram, lo que revoca nuestro acceso a esos datos.
        </p>
      </Section>

      <Section id="seguridad" title="10. Seguridad">
        <p>
          Aplicamos medidas técnicas y organizativas razonables (cifrado de credenciales,
          control de acceso, conexiones seguras HTTPS y validación de firmas de webhooks)
          para proteger la información contra acceso, alteración o divulgación no autorizados.
          Ningún sistema es completamente infalible, por lo que no podemos garantizar una
          seguridad absoluta.
        </p>
      </Section>

      <Section id="derechos" title="11. Derechos del titular">
        <p>
          De acuerdo con la Ley N.° 29733, el titular puede ejercer sus derechos de acceso,
          rectificación, cancelación y oposición (ARCO), así como revocar su consentimiento,
          escribiendo a <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>. Responderemos
          dentro de los plazos legales.
        </p>
      </Section>

      <Section id="transferencias" title="12. Transferencias internacionales">
        <p>
          Algunos proveedores (por ejemplo, Meta o proveedores de infraestructura) pueden
          procesar datos fuera del Perú. En tales casos adoptamos garantías adecuadas para
          proteger la información conforme a la normativa aplicable.
        </p>
      </Section>

      <Section id="cookies" title="13. Cookies">
        <p>
          Utilizamos cookies y almacenamiento local estrictamente necesarios para la
          autenticación y el funcionamiento de la Plataforma. No usamos cookies de
          publicidad de terceros.
        </p>
      </Section>

      <Section id="menores" title="14. Menores de edad">
        <p>
          La Plataforma está dirigida a empresas y a personas mayores de edad. No recopilamos
          de forma consciente datos de menores de edad para la creación de cuentas.
        </p>
      </Section>

      <Section id="cambios" title="15. Cambios a esta política">
        <p>
          Podemos actualizar esta Política de Privacidad. Publicaremos la versión vigente en
          esta página indicando la fecha de última actualización. El uso continuado del
          servicio implica la aceptación de la versión vigente.
        </p>
      </Section>

      <Section id="contacto" title="16. Contacto">
        <p>
          Para consultas sobre privacidad o para ejercer sus derechos, escriba a{" "}
          <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>.
        </p>
      </Section>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
